import axios from 'axios';
import { getApiUrl } from '../../../config/api';

/**
 * API client for Settings → Team & Roles and the invite link. The backend takes the
 * company from the signed-in identity, so nothing here sends one.
 */
const withCredentials = { withCredentials: true };

export async function getTeam() {
  const res = await axios.get(getApiUrl('/api/company/team'), withCredentials);
  return {
    people: Array.isArray(res.data?.people) ? res.data.people : [],
    seats: res.data?.seats || { used: 0, limit: null },
  };
}

/**
 * The roles and what the role editor needs. freePermissions is null from a server
 * without custom roles, and the screen then shows roles read-only.
 */
export async function getRoles() {
  const res = await axios.get(getApiUrl('/api/company/roles'), withCredentials);
  return {
    roles: Array.isArray(res.data?.roles) ? res.data.roles : [],
    permissions: Array.isArray(res.data?.permissions) ? res.data.permissions : [],
    freePermissions: Array.isArray(res.data?.free_permissions) ? res.data.free_permissions : null,
    you: res.data?.you || null,
    maxCustomRoles: res.data?.max_custom_roles ?? null,
  };
}

export async function createRole({ name, scope, permissions }) {
  const res = await axios.post(getApiUrl('/api/company/roles'), { name, scope, permissions }, withCredentials);
  return res.data;
}

/** version is what the role list returned; the server refuses if someone saved since. */
export async function updateRole(key, { name, scope, permissions, version }) {
  const res = await axios.put(
    getApiUrl(`/api/company/roles/${encodeURIComponent(key)}`),
    { name, scope, permissions, version },
    withCredentials
  );
  return res.data;
}

/** moveTo: where the role's people go. Needed when anyone has the role. */
export async function deleteRole(key, moveTo) {
  const res = await axios.delete(getApiUrl(`/api/company/roles/${encodeURIComponent(key)}`), {
    ...withCredentials,
    data: moveTo ? { move_to: moveTo } : {},
  });
  return res.data;
}

export async function invitePerson({ email, fullName, roleKey }) {
  const res = await axios.post(
    getApiUrl('/api/company/team/invite'),
    { email, full_name: fullName, role_key: roleKey },
    withCredentials
  );
  return res.data;
}

export async function changeRole(userId, roleKey) {
  await axios.patch(getApiUrl(`/api/company/team/${userId}`), { role_key: roleKey }, withCredentials);
}

export async function deactivatePerson(userId) {
  await axios.post(getApiUrl(`/api/company/team/${userId}/deactivate`), {}, withCredentials);
}

export async function reactivatePerson(userId) {
  await axios.post(getApiUrl(`/api/company/team/${userId}/reactivate`), {}, withCredentials);
}

export async function resendInvite(userId) {
  const res = await axios.post(getApiUrl(`/api/company/team/${userId}/invite`), {}, withCredentials);
  return res.data;
}

export async function cancelInvite(userId) {
  await axios.delete(getApiUrl(`/api/company/team/${userId}/invite`), withCredentials);
}

export async function getTeams() {
  const res = await axios.get(getApiUrl('/api/company/teams'), withCredentials);
  return {
    teams: Array.isArray(res.data?.teams) ? res.data.teams : [],
    people: Array.isArray(res.data?.people) ? res.data.people : [],
  };
}

export async function saveTeam({ id, name, managerUserId, memberIds }) {
  const body = { name, manager_user_id: managerUserId || null, member_ids: memberIds };
  const res = id
    ? await axios.put(getApiUrl(`/api/company/teams/${id}`), body, withCredentials)
    : await axios.post(getApiUrl('/api/company/teams'), body, withCredentials);
  return res.data;
}

export async function deleteTeam(id) {
  await axios.delete(getApiUrl(`/api/company/teams/${id}`), withCredentials);
}

/** Who works on a job, and who could. */
export async function getJobPeople(jobId) {
  const res = await axios.get(getApiUrl(`/api/jobs/${jobId}/team`), withCredentials);
  return {
    people: Array.isArray(res.data?.people) ? res.data.people : [],
    choices: Array.isArray(res.data?.choices) ? res.data.choices : [],
  };
}

/** people: [{ user_id, relation }]. Replaces who works on the job. */
export async function setJobPeople(jobId, people) {
  const res = await axios.put(getApiUrl(`/api/jobs/${jobId}/team`), { people }, withCredentials);
  return res.data;
}

// The accept page is public: the token in the body is the only credential.
export async function previewInvite(token) {
  const res = await axios.post(getApiUrl('/api/company/team/invite/preview'), { token });
  return res.data;
}

export async function acceptInvite({ token, password, fullName }) {
  await axios.post(getApiUrl('/api/company/team/invite/accept'), {
    token,
    password,
    full_name: fullName,
  });
}
